<template>
    <div class="contact-distributions-by-channel" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>

        <div class="chart-wrapper">
            <EChartsHorizontalBar
                :labels="getLabels()"
                :datasets="getDatasets()"
                :height="getChartHeight()"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="$t('report.mostUsedChannel')" :value="getStats().maxChannel" />
            <StatCard :title="$t('report.leastUsedChannel')" :value="getStats().minChannel" />
            <StatCard
                :title="$t('report.totalPersonCount')"
                :value="formatStat(getStats().total).value"
                unit="Kişi"
            />
        </div>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import EChartsHorizontalBar from "../../../components/charts/EChartsHorizontalBar.vue";
import { expertToExcel } from "../../../utils/utils";
import fscreen from "fscreen";

const channelTypeNames = {
    "whatsapp-business-api": "WhatsApp Business API",
    "whatsapp-business-api-supsis": "WhatsApp Business API V2",
    "whatsapp-qr": "WhatsApp QR",
    "whatsapp-v2": "WhatsApp V2",
    "instagram": "Instagram",
    "instagram-v2": "Instagram V2",
    "messenger": "Messenger",
    "trendyol": "Trendyol",
    "meta-leads": "Meta Leads",
    "whatsapp-unipile": "WhatsApp V3",
    "linkedin-unipile": "LinkedIn",
    "telegram-unipile": "Telegram",
};

export default {
    name: "ContactDistributionsByChannel",
    components: {
        IconButton,
        StatCard,
        TableView,
        EChartsHorizontalBar
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tableMode: false,
            fullScreen: false,
            cancels: [],
            loadingInstance: null
        };
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("report.contact")
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-channel");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("report.channel"),
                    dataIndex: "channel",
                    key: "channel",
                },
                {
                    title: this.$t("report.channelType"),
                    dataIndex: "type",
                    key: "type",
                },
                {
                    title: this.$t("report.contactCount"),
                    dataIndex: "count",
                    key: "count",
                }
            ];
        },
        getRows() {
            return (this.data || []).map(item => ({
                channel: item.name,
                type: channelTypeNames[item.type] || item.type,
                count: item.count
            }));
        },
        getStats() {
            const items = this.data || [];
            const maxChannel = _.maxBy(items, 'count')?.name || this.$t("report.noEnoughtData");
            const minChannel = _.minBy(items, 'count')?.name || this.$t("report.noEnoughtData");
            const total = _.sumBy(items, 'count') || 0;

            return {
                maxChannel,
                minChannel,
                total
            };
        },
        getLabels() {
            const items = this.data || [];
            return items.map(item => item.name);
        },
        getDatasets() {
            const items = this.data || [];
            const palette = [
                "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
                "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6",
                "#84CC16", "#E11D48", "#0EA5E9", "#D946EF", "#78716C",
            ];
            return [
                {
                    label: this.$t("report.contactCount"),
                    data: items.map(item => item.count),
                    backgroundColor: items.map((_, i) => palette[i % palette.length] + "B3"),
                    borderColor: items.map((_, i) => palette[i % palette.length]),
                    borderWidth: 1,
                    borderRadius: 4,
                },
            ];
        },
        getChartHeight() {
            const items = this.data || [];
            return Math.max(320, items.length * 35);
        },
        exportToExcel() {
            const excelData = this.getRows();
            expertToExcel(excelData, `channel_distributions_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        }
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".contact-distributions-by-channel"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach(cancel => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.contact-distributions-by-channel {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
